import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Rocket,
  Database,
  Settings,
  Phone,
  ClipboardList,
  SlidersHorizontal,
  CheckCircle2,
  Shield,
  Zap,
} from "lucide-react";

const weeks = [
  {
    week: 1,
    title: "PMS Go-Live & Migration",
    color: "blue",
    icon: Database,
    tasks: [
      "Migrate data from your current PMS to Asha",
      "Go live on Asha PMS from day one",
      "Order VoIP phones & any hardware",
      "Fill out configurations & train AI to your office",
    ],
  },
  {
    week: 2,
    title: "Front Office Go-Live",
    color: "indigo",
    icon: Phone,
    tasks: [
      "Go live with front office AI agents (Amy, Leena, etc.)",
      "Configure back office AI settings",
      "Begin ERA enrollments & payer setup",
      "Staff training on communication tools",
    ],
  },
  {
    week: 3,
    title: "Calibrate & Refine",
    color: "violet",
    icon: SlidersHorizontal,
    tasks: [
      "Adjust PMS workflows to your liking",
      "Fine-tune front office AI behavior & scripts",
      "Review call recordings & scheduling patterns",
      "Optimize templates, forms & automation rules",
    ],
  },
  {
    week: 4,
    title: "Back Office Go-Live",
    color: "emerald",
    icon: Rocket,
    tasks: [
      "Go live with back office AI & full RCM",
      "AI handles claims, denials, billing & collections",
      "Human billing expert begins strategic oversight",
      "Your practice is fully running on Asha",
    ],
  },
];

const colorMap = {
  blue: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    accent: "text-blue-400",
    iconBg: "bg-blue-500",
    dot: "bg-blue-400",
    glow: "shadow-blue-500/20",
    line: "from-blue-500",
  },
  indigo: {
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/30",
    accent: "text-indigo-400",
    iconBg: "bg-indigo-500",
    dot: "bg-indigo-400",
    glow: "shadow-indigo-500/20",
    line: "from-indigo-500",
  },
  violet: {
    bg: "bg-violet-500/10",
    border: "border-violet-500/30",
    accent: "text-violet-400",
    iconBg: "bg-violet-500",
    dot: "bg-violet-400",
    glow: "shadow-violet-500/20",
    line: "from-violet-500",
  },
  emerald: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    accent: "text-emerald-400",
    iconBg: "bg-emerald-500",
    dot: "bg-emerald-400",
    glow: "shadow-emerald-500/20",
    line: "from-emerald-500",
  },
};

const GoLiveTimelineSlide = () => {
  const [activeWeek, setActiveWeek] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const taskRefs = useRef({});

  const scrollToTask = useCallback((weekIdx, taskIdx) => {
    const key = `${weekIdx}-${taskIdx}`;
    const el = taskRefs.current[key];
    if (el) {
      // Find the closest scrollable parent (the overflow-y-auto div)
      let container = el.parentElement;
      while (container && getComputedStyle(container).overflowY !== "auto") {
        container = container.parentElement;
      }
      if (container) {
        const elTop = el.offsetTop;
        const elHeight = el.offsetHeight;
        const containerHeight = container.clientHeight;
        const scrollTarget = elTop - containerHeight + elHeight + 8;
        if (scrollTarget > container.scrollTop) {
          container.scrollTo({ top: scrollTarget, behavior: "smooth" });
        }
      }
    }
  }, []);

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        for (let w = 0; w < 4; w++) {
          setActiveWeek(w);
          setCompletedTasks(new Set());
          for (let t = 0; t < weeks[w].tasks.length; t++) {
            await new Promise((r) => setTimeout(r, 800));
            setCompletedTasks((prev) => new Set([...prev, `${w}-${t}`]));
            scrollToTask(w, t);
          }
          await new Promise((r) => setTimeout(r, 1200));
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    cycle();
  }, []);

  return (
    <div className="h-full w-full bg-[#0B1120] text-white flex flex-col px-8 py-6 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-600/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-indigo-600/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none" />

      {/* Header */}
      <div className="text-center mb-6 flex-shrink-0 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-sm font-semibold mb-2 border border-blue-500/20">
            <Rocket className="w-4 h-4" />
            <span>Getting Started</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-1">
            Live in{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-emerald-400">
              4 weeks
            </span>
            {" "}— everything included
          </h2>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto">
            From data migration to full AI-powered operations, we get your entire practice running on Asha in one month.
          </p>
        </motion.div>
      </div>

      {/* Timeline */}
      <div className="flex-1 flex items-center justify-center relative z-10 min-h-0">
        <div className="w-full max-w-[1400px]">

          {/* Progress bar connecting weeks */}
          <div className="relative mb-8">
            <div className="absolute top-5 left-[6%] right-[6%] h-1 bg-slate-800 rounded-full" />
            <motion.div
              className="absolute top-5 left-[6%] h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500 rounded-full"
              animate={{ width: `${(activeWeek / 3) * 88}%` }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            />

            {/* Week dots */}
            <div className="relative flex justify-between px-[4%]">
              {weeks.map((week, i) => {
                const c = colorMap[week.color];
                const isActive = activeWeek === i;
                const isPast = activeWeek > i;
                return (
                  <motion.div
                    key={i}
                    className="flex flex-col items-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <motion.div
                      className={`w-10 h-10 rounded-full flex items-center justify-center relative z-10 transition-all duration-500 ${
                        isActive
                          ? `${c.iconBg} shadow-lg ${c.glow}`
                          : isPast
                          ? "bg-emerald-500 shadow-md"
                          : "bg-slate-800 border-2 border-slate-700"
                      }`}
                      animate={isActive ? { scale: [1, 1.15, 1] } : {}}
                      transition={isActive ? { duration: 2, repeat: Infinity } : {}}
                    >
                      {isPast ? (
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      ) : (
                        <week.icon className="w-5 h-5 text-white" />
                      )}
                    </motion.div>
                    <span className={`mt-2 text-xs font-bold uppercase tracking-wider ${isActive ? c.accent : isPast ? "text-emerald-400" : "text-slate-600"}`}>
                      Week {week.week}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Week cards */}
          <div className="grid grid-cols-4 gap-4">
            {weeks.map((week, wi) => {
              const c = colorMap[week.color];
              const isActive = activeWeek === wi;
              const isPast = activeWeek > wi;
              const Icon = week.icon;

              return (
                <motion.div
                  key={wi}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + wi * 0.1 }}
                  className={`rounded-xl border backdrop-blur-sm p-4 transition-all duration-500 ${
                    isActive
                      ? `${c.bg} ${c.border} shadow-lg ${c.glow}`
                      : isPast
                      ? "bg-emerald-500/5 border-emerald-500/20"
                      : "bg-slate-800/40 border-slate-700/30"
                  }`}
                >
                  {/* Card header */}
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      isActive ? c.iconBg : isPast ? "bg-emerald-500/20" : "bg-slate-700/50"
                    }`}>
                      {isPast ? (
                        <CheckCircle2 className={`w-3.5 h-3.5 text-emerald-400`} />
                      ) : (
                        <Icon className={`w-3.5 h-3.5 ${isActive ? "text-white" : "text-slate-500"}`} />
                      )}
                    </div>
                    <h3 className={`text-sm font-bold ${isActive ? "text-white" : isPast ? "text-emerald-300" : "text-slate-500"}`}>
                      {week.title}
                    </h3>
                  </div>

                  {/* Tasks */}
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {week.tasks.map((task, ti) => {
                      const taskKey = `${wi}-${ti}`;
                      const isDone = completedTasks.has(taskKey) || isPast;
                      const isAnimating = isActive && !isDone;

                      return (
                        <motion.div
                          key={ti}
                          ref={(el) => { taskRefs.current[taskKey] = el; }}
                          className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg transition-all duration-300 ${
                            isDone
                              ? isActive ? `${c.bg} border border-transparent` : "bg-emerald-500/5"
                              : isAnimating
                              ? "bg-white/[0.03] border border-white/5"
                              : "bg-transparent"
                          }`}
                        >
                          <div className="mt-0.5 shrink-0">
                            {isDone ? (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 300 }}
                              >
                                <CheckCircle2 className={`w-3.5 h-3.5 ${isActive ? c.accent : "text-emerald-400"}`} />
                              </motion.div>
                            ) : (
                              <div className={`w-3.5 h-3.5 rounded-full border-2 ${isActive ? "border-slate-600" : "border-slate-700"}`} />
                            )}
                          </div>
                          <span className={`text-xs leading-relaxed ${
                            isDone
                              ? isActive ? "text-slate-200" : "text-slate-400"
                              : isActive ? "text-slate-400" : "text-slate-600"
                          }`}>
                            {task}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex items-center justify-center gap-5 mt-4 flex-shrink-0 relative z-10"
      >
        {[
          { icon: Zap, text: "White-glove onboarding included", color: "text-blue-400" },
          { icon: Shield, text: "Zero downtime migration", color: "text-emerald-400" },
          { icon: ClipboardList, text: "Dedicated implementation team", color: "text-violet-400" },
        ].map((stat, i) => (
          <div
            key={i}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/5"
          >
            <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
            <span className="text-xs font-semibold text-slate-400">{stat.text}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default GoLiveTimelineSlide;
